import { createTheme } from '@mui/material';

// Design System baseado no site Hoopers.club
export const theme = createTheme({
  palette: {
    primary: {
      main: '#FF6B35',      // Laranja principal Hoopers
      light: '#FF8A65',     // Laranja claro
      dark: '#E65100',      // Laranja escuro
      contrastText: '#FFFFFF'
    },
    secondary: {
      main: '#1976D2',      // Azul principal
      light: '#42A5F5',     // Azul claro
      dark: '#0D47A1',      // Azul escuro
      contrastText: '#FFFFFF'
    },
    background: {
      default: '#FAFAFA',   // Cinza muito claro
      paper: '#FFFFFF',     // Branco puro
    },
    text: {
      primary: '#212121',   // Preto suave
      secondary: '#757575', // Cinza médio
    },
    grey: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: '#9E9E9E',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica Neue", "Arial", sans-serif',
    h1: {
      fontSize: '3.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
      '@media (max-width:600px)': {
        fontSize: '2.5rem',
      },
    },
    h2: {
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
    h3: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.4,
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
      '@media (max-width:600px)': {
        fontSize: '1.25rem',
      },
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: '#757575',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: '#757575',
    },
    button: {
      fontWeight: 600,
      textTransform: 'none' as const,
      borderRadius: '8px',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0, 0, 0, 0.05)',
    '0px 4px 8px rgba(0, 0, 0, 0.1)',
    '0px 8px 16px rgba(0, 0, 0, 0.1)',
    '0px 12px 24px rgba(0, 0, 0, 0.15)',
    '0px 16px 32px rgba(0, 0, 0, 0.2)',
    ...Array(19).fill('none'),
  ] as any,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          padding: '12px 24px',
          fontSize: '1rem',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #FF6B35 0%, #E65100 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #E65100 0%, #D84315 100%)',
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
        },
        elevation2: {
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
  },
});

// Gradientes personalizados
export const gradients = {
  primary: 'linear-gradient(135deg, #FF6B35 0%, #E65100 100%)',
  secondary: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
  hero: 'linear-gradient(135deg, rgba(255, 107, 53, 0.9) 0%, rgba(25, 118, 210, 0.9) 100%)',
  background: 'linear-gradient(180deg, #FAFAFA 0%, #F5F5F5 100%)',
  card: 'linear-gradient(145deg, #FFFFFF 0%, #F8F9FA 100%)',
};

// Cores personalizadas
export const colors = {
  orange: {
    50: '#FFF3E0',
    100: '#FFE0B2',
    200: '#FFCC80',
    300: '#FFB74D',
    400: '#FFA726',
    500: '#FF6B35',
    600: '#E65100',
    700: '#D84315',
    800: '#BF360C',
    900: '#A52714',
  },
  blue: {
    50: '#E3F2FD',
    100: '#BBDEFB',
    200: '#90CAF9',
    300: '#64B5F6',
    400: '#42A5F5',
    500: '#1976D2',
    600: '#1565C0',
    700: '#0D47A1',
    800: '#0B3D91',
    900: '#082A5C',
  },
};

export const globalStyles = {
  '*': {
    boxSizing: 'border-box',
  },
  body: {
    margin: 0,
    padding: 0,
    fontFamily: '"Inter", "Roboto", "Helvetica Neue", "Arial", sans-serif',
    backgroundColor: '#FAFAFA',
    color: '#212121',
    lineHeight: 1.6,
  },
  html: {
    scrollBehavior: 'smooth',
  },
};
